import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useAuthGate } from "@/hooks/use-auth-gate"
import { seekerHomePath } from "@/lib/candidate"
import { Skeleton } from "@/components/ui/skeleton"
import HeroSection from "@/components/hero-section-3"

export const Route = createFileRoute("/")({
  component: LandingPage,
})

function LandingPage() {
  const gate = useAuthGate()
  const navigate = useNavigate()
  // Google/cross-domain auth returns to SITE_URL (`/`) with `?ott=`. Wait for
  // the session before treating this as a logged-out marketing visit.
  const [ottWait, setOttWait] = useState(() => {
    if (typeof window === "undefined") return false
    return new URLSearchParams(window.location.search).has("ott")
  })

  useEffect(() => {
    if (!ottWait) return
    // Only a resolved *session* ends the wait. "loading" is the first commit on
    // every load, so clearing on it would drop the guard before Better Auth had
    // even started exchanging the token.
    if (gate.status === "ready" || gate.status === "no-profile") {
      setOttWait(false)
      return
    }
    const t = window.setTimeout(() => setOttWait(false), 2500)
    return () => window.clearTimeout(t)
  }, [gate.status, ottWait])

  useEffect(() => {
    if (ottWait || gate.status === "loading") return
    if (gate.status === "no-profile") {
      void navigate({ to: "/onboarding/role" })
      return
    }
    if (gate.status !== "ready") return
    if (gate.profile.role === "employer") {
      void navigate({ to: "/recruit" })
      return
    }
    void navigate({ to: seekerHomePath(gate.profile) })
  }, [gate, navigate, ottWait])

  // Don't flash the dual-door marketing shell for signed-in members (or while
  // their profile is still resolving) — that race sent Browse jobs clicks to
  // /recruit or /login before the board could stick.
  if (
    ottWait ||
    gate.status === "loading" ||
    gate.status === "ready" ||
    gate.status === "no-profile"
  ) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16">
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  return <HeroSection />
}
